/**
 * `GET /survey/catalog?kind=` — every standard question the editor may offer, for one survey kind,
 * and the cache prefix every survey-template read sits under.
 *
 * ONE definition for both hosts: `/raporty`'s editor reads it for the kind it opened, the public form
 * builder for `leadForm`, and the two share one cache entry per kind. It moved here from
 * `pages/reports/api/reportsQueries` on 27 September 2026, when a second copy of the lead form's
 * entry had grown in this feature under the same key.
 *
 * The KIND is a parameter rather than a narrowing of one response, and R20 is the reason: a check-in
 * never includes the four locked demographic questions, and the backend is the authority on which
 * catalog entries are available to which kind (`StandardQuestion.IsAvailableFor`). Filtering one
 * response here would be a second opinion, and the two would disagree the day a question moved.
 *
 * The catalog is the same shelf of system questions for every trainer and changes on a deploy, so it
 * takes the read-only window rather than a shorter one.
 */

import { queryOptions } from '@tanstack/react-query';

import { surveyTemplatesCacheRoot } from '~/entities/survey-template';
import { getSurveyCatalog } from '~/shared/api/generated/endpoints/survey/survey';
import { TrainWithMeDataEnumsSurveyKind } from '~/shared/api/generated/model';

import type { SurveyKindKey } from '../model/editorDraft';

/**
 * The prefix every survey-template read sits under — Raporty's lists and forms, this catalog and the
 * shelf. A write that changes a template (either host's save) invalidates it whole. Owned by
 * `entities/survey-template` since the templates list gained a reader outside `/raporty`.
 */
export { surveyTemplatesCacheRoot };

const CATALOG_STALE_TIME = 5 * 60 * 1000;

const CATALOG_KIND: Readonly<Record<SurveyKindKey, TrainWithMeDataEnumsSurveyKind>> = {
  intake: TrainWithMeDataEnumsSurveyKind.Intake,
  checkIn: TrainWithMeDataEnumsSurveyKind.CheckIn,
  leadForm: TrainWithMeDataEnumsSurveyKind.LeadForm,
};

export function surveyCatalogQueryKey(kind: SurveyKindKey) {
  return [...surveyTemplatesCacheRoot, 'catalog', kind] as const;
}

export function surveyCatalogQueryOptions(kind: SurveyKindKey) {
  return queryOptions({
    queryKey: surveyCatalogQueryKey(kind),
    queryFn: ({ signal }) => getSurveyCatalog({ kind: CATALOG_KIND[kind] }, undefined, signal),
    staleTime: CATALOG_STALE_TIME,
  });
}
